import React from 'react';
import { Row, Col, Card, Typography } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * Bitta statistik card
 *
 * item shape:
 * {
 *   label   : string          - sarlavha
 *   value   : string | number - asosiy qiymat
 *   icon    : ReactNode        - o'ng tomonidagi ikonka
 *   bg      : string           - karta fon rangi   (default: '#eef2ff')
 *   color   : string           - ikonka rangi      (default: '#667eea')
 *   prefix  : string           - qiymat oldidan    (default: '')
 *   suffix  : string           - qiymatdan keyin   (default: '')
 *   trend   : number           - foiz o'zgarishi, manfiy = kamayish
 *   span    : object           - antd Col span     (override per-item)
 * }
 */
const StatCard = ({ label, value, icon, bg, color = '#667eea', prefix = '', suffix = '', trend }) => (
  <Card
    style={{
      // Tint derived from `color` so it works in both light & dark themes
      // (legacy pastel `bg` values were solid light → invisible text in dark mode)
      background: (typeof color === 'string' && /^#[0-9a-fA-F]{6}$/.test(color)) ? color + '22' : (bg || 'var(--qk-secondary)'),
      border: '1px solid ' + color, borderRadius: 14, height: '100%'
    }}
    styles={{ body: { padding: '18px 20px' } }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div style={{ minWidth: 0 }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {label}
        </Text>
        <div
          style={{
            fontSize: 26,
            fontWeight: 700,
            marginTop: 6,
            color: 'var(--qk-text)',
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {prefix}{value}{suffix}
        </div>
        {trend !== undefined && trend !== null && (
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
            {trend >= 0 ? (
              <ArrowUpOutlined style={{ color: '#22c55e', fontSize: 12 }} />
            ) : (
              <ArrowDownOutlined style={{ color: '#ef4444', fontSize: 12 }} />
            )}
            <Text
              style={{
                fontSize: 12,
                color: trend >= 0 ? '#22c55e' : '#ef4444',
              }}
            >
              {Math.abs(trend)}% o&apos;tgan davrga nisbatan
            </Text>
          </div>
        )}
      </div>

      {icon && (
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            background: 'var(--qk-card-bg)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 20,
            color,
            flexShrink: 0,
            marginLeft: 12,
          }}
        >
          {icon}
        </div>
      )}
    </div>
  </Card>
);

/**
 * Stats — statistik cardlar to'plami
 *
 * Props:
 *   items    : StatCard[] — yuqoridagi stat obyektlari massivi
 *   gutter   : [h, v]     — Col oralig'i (default: [12, 12])
 *   xs / sm / md / lg / xl — responsive span (default: xs=24, sm=12, xl=6)
 *   style    : CSSProperties
 */
const Stats = ({
  items = [],
  gutter = [12, 12],
  xs = 24,
  sm = 12,
  md,
  lg,
  xl = 6,
  style,
}) => (
  <Row gutter={gutter} style={style}>
    {items.map((item, index) => (
      <Col
        key={item.key ?? index}
        xs={item.span?.xs ?? xs}
        sm={item.span?.sm ?? sm}
        md={item.span?.md ?? md}
        lg={item.span?.lg ?? lg}
        xl={item.span?.xl ?? xl}
      >
        <StatCard {...item} />
      </Col>
    ))}
  </Row>
);

export default Stats;
